import { Button, Card, Col, Dropdown, Empty, Input, Menu, Modal, Radio, Row, Spin } from 'antd';
import { SearchOutlined, EllipsisOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components/macro';

import { TemplateDto, TemplateDefaultSlot } from '../../api/transportLayer';
import { PageTitle } from '../../components/PageTitle';
import { useRootStore } from '../../context/storeContext';
import { UserContainer } from '../Common/styles';

const OrganizationTemplatesContainer = styled(UserContainer)`
  & .gd-templates-content {
    padding-top: 20px;
  }

  & .gd-template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
    width: 100%;
  }

  & .gd-template-card {
    background: ${({ theme: { colors } }) => colors.white};
    border-radius: 4px;
    border: 1px solid #f0f0f0;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
    }
    
    .ant-card-body {
      padding: 0;
    }
  }

  & .gd-template-preview-wrapper {
    position: relative;
    width: 100%;
    height: 240px;
    background: #e6eaed;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .gd-template-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  & .gd-template-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: white;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 10;
    font-size: 14px;
  }

  & .gd-template-info {
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
  }

  & .gd-template-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .gd-template-title {
    font-size: 16px;
    font-weight: 600;
    color: #111;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .gd-template-subtitle {
    font-size: 13px;
    color: #a0a0a0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .gd-template-actions-menu {
    font-size: 24px;
    color: #888;
    cursor: pointer;
    padding: 4px 0 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #333;
    }
  }

  & .gd-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
  }

  & .gd-error-message {
    color: ${({ theme: { colors } }) => colors.mediumRed};
    text-align: center;
    padding: 20px;
  }
`;


export const OrganizationTemplates = observer(() => {
  const { templatesStore } = useRootStore();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [setDefaultModalTemplate, setSetDefaultModalTemplate] =
    useState<TemplateDto | null>(null);
  const [defaultSlotChoice, setDefaultSlotChoice] =
    useState<TemplateDefaultSlot>('First');
  const [savingDefault, setSavingDefault] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templatesStore.templates;
    const query = searchQuery.trim().toLowerCase();
    return templatesStore.templates.filter((tpl) =>
      (tpl.title ?? '').toLowerCase().includes(query)
    );
  }, [templatesStore.templates, searchQuery]);

  useEffect(() => {
    templatesStore.fetchTemplates();
  }, [templatesStore]);

  const openSetDefaultModal = (template: TemplateDto) => {
    const initialChoice: TemplateDefaultSlot = template.firstDefault
      ? 'First'
      : template.secondDefault
        ? 'Second'
        : 'First';
    setDefaultSlotChoice(initialChoice);
    setSetDefaultModalTemplate(template);
  };

  const closeSetDefaultModal = () => {
    if (!savingDefault) {
      setSetDefaultModalTemplate(null);
    }
  };

  const handleSaveDefault = async () => {
    if (!setDefaultModalTemplate) {
      return;
    }
    setSavingDefault(true);
    try {
      const resp = await templatesStore.setDefaultTemplate(
        setDefaultModalTemplate.id,
        defaultSlotChoice
      );
      if (resp.succeeded) {
        setSetDefaultModalTemplate(null);
      }
    } finally {
      setSavingDefault(false);
    }
  };

  const handleEditClick = (template: TemplateDto) => {
    history.push(`/organization-templates/canvas-builder/${template.id}`);
  };

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    await templatesStore.fetchGuidedDoingTemplates();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTemplateSelect = (templateId: string) => {
    history.push(`/organization-templates/create/${templateId}`);
    handleCloseModal();
  };


  // Render the list of all templates of the organization templates page  
  const renderContent = () => {
    if (templatesStore.loading) {
      return (
        <div className="gd-loading-container">
          <Spin size="large" tip="Loading templates..." />
        </div>
      );
    }

    if (templatesStore.error) {
      return <div className="gd-error-message">{templatesStore.error}</div>;
    }

    if (templatesStore.templates.length === 0) {
      return (
        <Empty description="No templates available" style={{ marginTop: 40 }} />
      );
    }

    return (
      <div>
        <Input
          placeholder="Search by template title..."
          prefix={<SearchOutlined />}
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ marginBottom: 12, maxWidth: 320 }}
        />

        {filteredTemplates.length === 0 ? (
          <Empty description="No matching templates" style={{ marginTop: 40 }} />
        ) : (
          <div className="gd-template-grid">
            {filteredTemplates.map((template: TemplateDto, index: number) => (
              <Card
                key={`${template.tenantId}-${index}`}
                className="gd-template-card"
                hoverable
                onClick={() => handleEditClick(template)}
                bodyStyle={{ padding: 0 }}
              >
                <div className="gd-template-preview-wrapper">
                  {template.previewUrl ? (
                    <img
                      alt={template.title}
                      src={template.previewUrl}
                      className="gd-template-preview"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 0H124C132.837 0 140 7.16344 140 16V45L110 70L80 45L50 70L20 45L0 61.6667V16C0 7.16344 7.16344 0 16 0Z" fill="white" />
                      <path d="M0 80.3333L20 63.6667L50 88.6667L80 63.6667L110 88.6667L140 63.6667V124C140 132.837 132.837 140 124 140H16C7.16344 140 0 132.837 0 124V80.3333Z" fill="white" />
                    </svg>
                  )}
                  {(template.firstDefault || template.secondDefault) && (
                    <div
                      className="gd-template-badge"
                      style={{ backgroundColor: template.firstDefault ? '#52c41a' : '#1890ff' }}
                      title={template.firstDefault ? "First Default" : "Second Default"}
                    >
                      {template.firstDefault ? '1' : '2'}
                    </div>
                  )}
                </div>
                
                <div className="gd-template-info">
                  <div className="gd-template-text">
                    <h3 className="gd-template-title" title={template.title}>
                      {template.title}
                    </h3>
                    <p className="gd-template-subtitle">Organization Template</p>
              </div>

              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="setDefault"
                      onClick={(e) => {
                        e.domEvent.stopPropagation();
                        openSetDefaultModal(template);
                      }}
                    >
                      {template.firstDefault ? 'Change Default' : 'Set Default'}
                    </Menu.Item>
                  </Menu>
                    }
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <div
                      className="gd-template-actions-menu"
                      onClick={e => e.stopPropagation()}
                    >
                    <EllipsisOutlined />
                  </div>
                </Dropdown>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <OrganizationTemplatesContainer>
      <Col xs={24} sm={24} md={22} lg={20} xl={20} xxl={18}>
        <Row justify="space-between" align="middle">
          <PageTitle title="Organization Templates" />
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button type="primary" onClick={handleOpenModal}>
              Create from Template
            </Button>
            <Button
              type="primary"
              onClick={() =>
                history.push('/organization-templates/canvas-builder')
              }
            >
              Create with Canvas
            </Button>
          </div>
        </Row>
        <Row className="gd-templates-content">
          <Col span={24}>{renderContent()}</Col>
        </Row>
      </Col>


      {/* Modal for creating a new template from existing templates */}
      <Modal
        title="Select a Template"
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {templatesStore.loading ? (
          <div className="gd-loading-container">
            <Spin size="large" tip="Loading templates..." />
          </div>
        ) : templatesStore.guidedDoingTemplates.length === 0 ? (
          <Empty description="No templates available for guided-doing" />
        ) : (
          <div className="gd-template-grid">
            {templatesStore.guidedDoingTemplates.map(
              (template: TemplateDto, index: number) => (
                <Card
                  key={`${template.tenantId}-${index}`}
                  className="gd-template-card"
                  hoverable
                  onClick={() => handleTemplateSelect(template.id)}
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="gd-template-preview-wrapper">
                    {template.previewUrl ? (
                      <img
                        alt={template.title}
                        src={template.previewUrl}
                        className="gd-template-preview"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 0H124C132.837 0 140 7.16344 140 16V45L110 70L80 45L50 70L20 45L0 61.6667V16C0 7.16344 7.16344 0 16 0Z" fill="white" />
                        <path d="M0 80.3333L20 63.6667L50 88.6667L80 63.6667L110 88.6667L140 63.6667V124C140 132.837 132.837 140 124 140H16C7.16344 140 0 132.837 0 124V80.3333Z" fill="white" />
                      </svg>
                    )}
                    {(template.firstDefault || template.secondDefault) && (
                      <div
                        className="gd-template-badge"
                        style={{ backgroundColor: template.firstDefault ? '#52c41a' : '#1890ff' }}
                        title={template.firstDefault ? "First Default" : "Second Default"}
                      >
                        {template.firstDefault ? '1' : '2'}
                      </div>
                    )}
                  </div>
                  <div className="gd-template-info">
                    <div className="gd-template-text">
                      <h3 className="gd-template-title" title={template.title}>
                        {template.title}
                      </h3>
                      <p className="gd-template-subtitle">Organization Template</p>
                    </div>
                  </div>
                </Card>
              )
            )}
          </div>
        )}
      </Modal>

      {/* Modal for setting first / second default template */}
      <Modal
        title="Set organization default"
        open={!!setDefaultModalTemplate}
        onCancel={closeSetDefaultModal}
        maskClosable={!savingDefault}
        closable={!savingDefault}
        destroyOnClose
        footer={[
          <Button key="cancel" disabled={savingDefault} onClick={closeSetDefaultModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={savingDefault}
            onClick={handleSaveDefault}
          >
            Save
          </Button>,
        ]}
      >
        {setDefaultModalTemplate && (
          <div style={{ marginBottom: 16 }}>
            <div className="gd-template-title" title={setDefaultModalTemplate.title}>
              {setDefaultModalTemplate.title}
            </div>
          </div>
        )}
        <Radio.Group
          value={defaultSlotChoice}
          onChange={(e) => setDefaultSlotChoice(e.target.value)}
          disabled={savingDefault}
        >
          <Radio value="First">First default</Radio>
          <Radio value="Second">Second default</Radio>
        </Radio.Group>
      </Modal>
    </OrganizationTemplatesContainer>
  );
});
